import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Handles, Rail, Slider, Ticks, Tracks } from 'react-compound-slider';
import { endOfToday, format, getHours, isBefore, startOfToday, } from 'date-fns';

import DateTick from './Components/DateTick';
import SliderRail from './Components/SliderRail';
import Track from './Components/Track';
import Tick from './Components/Tick';
import Handle from './Components/Handle';
import { getTimelineDivisions, intervalDuration, intervalToTimestampArray } from "../../Helpers/IntervalHelper";
import NowTrack from "./Components/NowTrack";
import { DateFormat, getHumanReadableDate } from "../../Helpers/DateTimeHelper";
import BlockedIntervalTracks from "./Components/BlockedIntervalTracks";


const getClickedTimeFromElement = (event, timelineInterval) => {
    const minutesPerInterval = 15;

    const { left, width } = event.currentTarget.getBoundingClientRect();

    const normalizedClickPosition = (event.clientX - left) / width;
    const domain = intervalToTimestampArray(timelineInterval);
    const clickedTime = domain[0] + normalizedClickPosition * (domain[1] - domain[0]);
    const roundingInterval = minutesPerInterval * 60 * 1000;

    return Math.round(clickedTime / roundingInterval) * roundingInterval;
}

/**
 * Provides a timeline range slider. This component is solely
 * respnsible for the rendering and interaction of the
 * timeline range, validation of the selected range does not happen
 * in this component. This must happen in a parent component.
 * AvailabilityTimelineInput already does this
 * @param props
 * @returns {JSX.Element}
 * @constructor
 */
const TimeRangeSlider = (props) => {

    // TODO - refactor to use UseMouseDragTimeSelection hook
    const [isMouseDown, setMouseDown] = useState(false);
    const [pivotDateTime, setPivotDateTime] = useState(null);

    const formatTicks = (ms) => {
        if (format(new Date(ms), 'H') == 12) {
            return format(new Date(ms), 'h a');
        }
        return format(new Date(ms), 'H');
    }

    const formatDateTick = (ms) => {
        return getHumanReadableDate(new Date(ms), DateFormat.HUMAN_SLASHED_DATE_WITH_WEEKDAY_ABBREVIATED)
    }

    const isMidnight = (ms) => {
        return getHours(new Date(ms)) == 0;
    }


    const handleMouseDown = (event) => {
        const clickedTime = getClickedTimeFromElement(event, props.timelineInterval);
        const previousDuration = intervalDuration(props.selectedInterval);
        const newDateObj = new Date(clickedTime);

        setMouseDown(true);
        setPivotDateTime(newDateObj);

        props.onChange({
            start: newDateObj,
            end: new Date(clickedTime + previousDuration)
        })
    }

    const handleMouseUp = (event) => {
        setMouseDown(false);
        setPivotDateTime(null);
    }

    const handleMouseMove = (event) => {
        if (!isMouseDown) {
            return;
        }
        const clickedTime = getClickedTimeFromElement(event, props.timelineInterval);
        const newDateObj = new Date(clickedTime);

        if (pivotDateTime && isBefore(newDateObj, pivotDateTime)) {
            props.onChange({
                start: newDateObj,
                end: pivotDateTime,
            })
        } else {
            props.onChange({
                start: props.selectedInterval.start,
                end: newDateObj
            })
        }
    }

    const handleMouseLeave = (event) => {
        setMouseDown(false);
        setPivotDateTime(null);
    }

    return (
        <div className={props.containerClassName || 'react_time_range__time_range_container' }
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseLeave}
        >
            <Slider
                mode={props.mode}
                step={props.step}
                domain={intervalToTimestampArray(props.timelineInterval)}
                disabled={(!props.disabled || !props.showHandles)}
                values={intervalToTimestampArray(props.selectedInterval)}
                rootStyle={{ position: 'relative', width: '100%' }}
            >
                <Rail>
                    {({ getRailProps }) =>
                        <SliderRail
                            className={props.sliderRailClassName}
                            getRailProps={getRailProps}
                        />}
                </Rail>

                {(!props.disabled && props.showHandles) &&
                    <Handles>
                        {({ handles, getHandleProps }) => (<>
                            {handles.map(handle => (
                                <Handle
                                    error={props.error}
                                    key={handle.id}
                                    handle={handle}
                                    domain={intervalToTimestampArray(props.timelineInterval)}
                                    getHandleProps={getHandleProps}
                                />
                            ))}
                        </>)}
                    </Handles>
                }


                <Tracks left={false} right={false}>
                    {({ tracks, getTrackProps }) => (<>
                        {tracks?.map(({ id, source, target }) => {

                            // Hack to hide the selected range if component
                            // is marked as disabled. Assumes that first
                            // track is selected range
                            if (props.disabled && id === "$$-0-$$-1") {
                                return;
                            }

                            return <Track
                                error={props.error}
                                key={id}
                                sourceOffset={source.percent}
                                targetOffset={target.percent}
                                getTrackProps={getTrackProps}
                                className="timeslot-selected"
                            />
                        })}
                    </>)}
                </Tracks>

                <BlockedIntervalTracks
                    timelineInterval={props.timelineInterval}
                    blockedIntervals={props.blockedIntervals}
                    showTooltips={!isMouseDown}
                />

                {props.showNow && (
                    <Tracks left={false} right={false}>
                        {({ getTrackProps }) => (
                            <NowTrack
                                timelineInterval={props.timelineInterval}
                                getTrackProps={getTrackProps}
                            />
                        )}
                    </Tracks>
                )}

                <Ticks
                    values={getTimelineDivisions(props.timelineInterval)}
                >
                    {({ ticks }) => (
                        <>
                            {ticks.map(tick => (
                                <React.Fragment key={tick.id}>
                                    <Tick
                                        tick={tick}
                                        count={ticks.length}
                                        format={formatTicks}
                                    />

                                    { (tick.percent === 0 || isMidnight(tick.value)) &&
                                        <DateTick
                                            tick={tick}
                                            count={ticks.length}
                                            format={formatDateTick}
                                        />
                                    }
                                </React.Fragment>
                            ))}
                        </>
                    )}
                </Ticks>
            </Slider>
        </div>
    )
}

TimeRangeSlider.propTypes = {
    selectedInterval: PropTypes.object,
    timelineInterval: PropTypes.object,
    blockedIntervals: PropTypes.arrayOf(PropTypes.object),
    containerClassName: PropTypes.string,
    sliderRailClassName: PropTypes.string,
    error: PropTypes.any,

    step: PropTypes.number,
    ticksNumber: PropTypes.number,
    mode: PropTypes.number,
    showNow: PropTypes.bool,
    maxDuration: PropTypes.number,
    allowPast: PropTypes.bool,
    disabled: PropTypes.bool,
    showHandles: PropTypes.bool,

    onUpdateCallback: PropTypes.func,
    onChange: PropTypes.func,
}

TimeRangeSlider.defaultProps = {
    selectedInterval: [],
    timelineInterval: [startOfToday(), endOfToday()],
    blockedIntervals: [],
    error: false,

    step: 1000*60*30,
    ticksNumber: 48,
    mode: 3,
    showNow: false,
    maxDuration: 0,
    allowPast: true,
    showHandles: true,
}

export default TimeRangeSlider
